import React, { useEffect } from "react";
import { Box, Button, useTheme } from "@mui/material";
import MUIDataTable from "mui-datatables";
import Header from "components/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchListAbsensiHariini } from "state/absensiActions";
import { FilterList } from "@mui/icons-material";
import CustomFilterModal from "./CustomFilterModal";
import SortableHeader from "components/SortableHeader";
import LokasiCell from "components/Modals/LokasiCell";
import moment from "moment";
import "moment/locale/id";
moment.locale("id");

const AbsensiHariIni = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openCustomFilter, setOpenCustomFilter] = React.useState(false);
  const { list_absensi_hariini } = useSelector((state) => state.absensi);
  const [filteredData, setFilteredData] = React.useState([]);
  const [appliedFilters, setAppliedFilters] = React.useState({});
  const [rowsPerPage, setRowsPerPage] = React.useState(30);

  const columnSort = [
    { value: "id_karyawan", label: "ID Karyawan" },
    { value: "nama_karyawan", label: "Nama Karyawan" },
    { value: "waktu_pagi", label: "Waktu Absen" },
    { value: "waktu_siang", label: "Waktu Absen" },
    { value: "waktu_pulang", label: "Waktu Pulang" },
    { value: "telat_menit", label: "Telat Menit" },
  ];

  useEffect(() => {
    dispatch(fetchListAbsensiHariini());
  }, []);

  useEffect(() => {
    // Apply filters whenever data or filters change
    if (list_absensi_hariini) {
      const filtered = applyCustomFilters(list_absensi_hariini, appliedFilters);
      setFilteredData(filtered);
    }
  }, [list_absensi_hariini, appliedFilters]);
  const handleApplyCustomFilter = (filters) => {
    setAppliedFilters(filters);
  };

  // Main filter logic
  const applyCustomFilters = (data, filterConfig) => {
    if (!filterConfig || Object.keys(filterConfig).length === 0) return data;

    let result = [...data];

    // Apply Search filter (hanya 1 string, tapi cek ke 2 kolom)
    if (filterConfig.search && filterConfig.search.trim() !== "") {
      const searchValue = filterConfig.search.toLowerCase();
      result = result.filter((row) => {
        const idMatch = String(row.id_karyawan || "")
          .toLowerCase()
          .includes(searchValue);
        const namaMatch = String(row.nama_karyawan || "")
          .toLowerCase()
          .includes(searchValue);
        return idMatch || namaMatch;
      });
    }

    // Apply Category filters
    if (filterConfig.filter) {
      if (filterConfig.filter.status && filterConfig.filter.status.length > 0) {
        result = result.filter((row) => {
          return filterConfig.filter.status.includes(row.status);
        });
      }

      if (
        filterConfig.filter.shift_absen &&
        filterConfig.filter.shift_absen.length > 0
      ) {
        result = result.filter((row) => {
          return filterConfig.filter.shift_absen.includes(row.shift_absen);
        });
      }

      if (
        filterConfig.filter.location_check_in &&
        filterConfig.filter.location_check_in.length > 0
      ) {
        result = result.filter((row) => {
          return filterConfig.filter.location_check_in.includes(
            row.location_check_in,
          );
        });
      }
    }

    // Apply Sort
    if (filterConfig.sort && filterConfig.sort.length > 0) {
      result.sort((a, b) => {
        for (let sortRule of filterConfig.sort) {
          let { column, order } = sortRule;
          let aValue = a[column];
          let bValue = b[column];

          if (aValue == null) return 1;
          if (bValue == null) return -1;

          if (typeof aValue === "string") aValue = aValue.toLowerCase();
          if (typeof bValue === "string") bValue = bValue.toLowerCase();

          if (aValue < bValue) return order === "asc" ? -1 : 1;
          if (aValue > bValue) return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  };

  const handleClearCustomFilter = () => {
    setAppliedFilters({});
    setFilteredData([]);
  };
  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;

    // ✅ Search
    if (appliedFilters.search && appliedFilters.search.trim() !== "") {
      count++;
    }

    // ✅ Filter
    if (appliedFilters.filter) {
      if (appliedFilters.filter.nama_jabatan?.length > 0) count++;
      if (appliedFilters.filter.nama_cab?.length > 0) count++;
    }

    // ✅ Sort (multiple)
    if (
      Array.isArray(appliedFilters.sort) &&
      appliedFilters.sort.some(
        (item) => item.column && item.column.trim() !== "",
      )
    ) {
      count++;
    }

    return count;
  };

  const renderStatus = (status) => {
    if (status === "Absen Pagi" || status === "Absen Siang") {
      return "Sedang Bekerja";
    } else if (status === "Absen Sore") {
      return "Selesai Bekerja";
    } else {
      return "Sedang Izin";
    }
  };

  const columnsKaryawan = [
    {
      name: "id_karyawan",
      label: "ID Karyawan",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
      },
    },
    {
      name: "nama_karyawan",
      label: "Nama Karyawan",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
      },
    },
    {
      name: "shift_absen",
      label: "Shift Absensi",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value) => {
          return value;
        },
      },
    },

    {
      name: "waktu_pagi",
      label: "Waktu Absen Pagi",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value, tableMeta) => {
          const shift_absen = tableMeta.rowData[2];
          let cutoffTime;
          if (!value) {
            return <span style={{ color: "gray" }}>-</span>;
          }
          const actualTime = moment(value);
          switch (shift_absen) {
            case "Shift 1 Pagi":
              cutoffTime = moment("07:00:00", "HH:mm:ss");
              break;
            case "Shift 2 Siang":
              cutoffTime = moment("14:00:00", "HH:mm:ss");
              break;
            case "Shift Middle":
              cutoffTime = moment("11:00:00", "HH:mm:ss");
              break;
            default:
              cutoffTime = moment("08:00:00", "HH:mm:ss");
          }

          cutoffTime
            .year(actualTime.year())
            .month(actualTime.month())
            .date(actualTime.date());

          const isLate = actualTime.isAfter(cutoffTime);

          const style = isLate
            ? { color: "red", fontWeight: "bold" }
            : { color: "black" };

          const formattedTime = actualTime.format("HH:mm:ss");

          return <span style={style}>{formattedTime}</span>;
        },
      },
    },
    {
      name: "waktu_siang",
      label: "Waktu Absen Kedua",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value) => {
          if (!value) {
            return <span style={{ color: "gray" }}>-</span>;
          }
          const actualTime = moment(value);
          const cutoffTime = moment("15:00:00", "HH:mm:ss");
          cutoffTime
            .year(actualTime.year())
            .month(actualTime.month())
            .date(actualTime.date());
          const isLate = actualTime.isAfter(cutoffTime);
          const style = isLate
            ? { color: "red", fontWeight: "bold" }
            : { color: "black" };
          const formattedTime = actualTime.format("HH:mm:ss");
          return <span style={style}>{formattedTime}</span>;
        },
      },
    },
    {
      name: "waktu_pulang",
      label: "Waktu Absen Pulang",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value) => {
          if (!value) {
            return "-";
          }
          return moment(value).format("HH:mm:ss");
        },
      },
    },
    {
      name: "status",
      label: "Status Absensi",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value) => {
          return renderStatus(value);
        },
      },
    },
    {
      name: "location_check_in",
      label: "Lokasi Check In",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value) => {
          return <LokasiCell text={value} />;
        },
      },
    },
    {
      name: "telat_menit",
      label: "Menit Telat",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value) => value + " Menit",
      },
    },
  ];

  const options = {
    selectableRows: "none",
    filterType: "dropdown",
    filter: false,
    fixedHeader: false,
    fixedSelectColumn: false,
    search: true,
    viewColumns: false,
    responsive: "standard",
    customToolbar: () => {
      const activeCount = getActiveFilterCount();
      return (
        <Button
          startIcon={<FilterList />}
          onClick={() => setOpenCustomFilter(true)}
          variant={activeCount > 0 ? "contained" : "outlined"}
          size="small"
          color={activeCount > 0 ? "primary" : "inherit"}
          sx={{
            mr: 1,
            ...(activeCount === 0 && {
              color: "text.secondary",
              borderColor: "text.secondary",
            }),
          }}
        >
          Filter {activeCount > 0 && `(${activeCount})`}
        </Button>
      );
    },
    rowsPerPageOptions: [
      30,
      60,
      filteredData.length > 0 || Object.keys(appliedFilters).length > 0
        ? filteredData.length
        : list_absensi_hariini.length,
    ],
    rowsPerPage: rowsPerPage,
    onChangeRowsPerPage: (numberOfRows) => setRowsPerPage(numberOfRows),
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title={`Absensi Hari Ini`}
        subtitle={`Daftar absensi karyawan hari ini (${moment().format(
          "DD MMMM YYYY",
        )})`}
      />

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <MUIDataTable
          title={""}
          data={
            filteredData.length > 0 || Object.keys(appliedFilters).length > 0
              ? filteredData
              : list_absensi_hariini
          }
          columns={columnsKaryawan}
          options={options}
        />
        {/* Custom Filter Modal */}
        <CustomFilterModal
          open={openCustomFilter}
          onClose={() => setOpenCustomFilter(false)}
          onApplyFilter={handleApplyCustomFilter}
          onClearFilter={handleClearCustomFilter}
          data={list_absensi_hariini}
          sortableColumns={columnSort}
        />
      </Box>
    </Box>
  );
};

export default AbsensiHariIni;
